import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';

/**
 * S7 PR1 (§4.2/§9) — useDelegateSteps.
 *
 * Mutation hook para abrir a delegação POR ETAPA (S5, ADR-154) a partir do
 * `DelegateStepsModal`. Molde 1:1 de `use-take-over.ts`: invalida o detail
 * da conversa em sucesso (o campo `delegation` + `operationMode.current`
 * chegam atualizados — a conversa flipa para H_c e o `AiConductingBanner`
 * assume com o sub-estado `delegating`).
 *
 * Capability gate backend: `ai-step-delegation` (403 sem ela — o botão que
 * abre o modal já é gated no Composer, mas o hook não assume nada).
 */
export function useDelegateSteps(conversationId: string | undefined): {
  mutate: (body: { objectives?: string[]; timeoutMinutes?: number }) => Promise<void>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (body: { objectives?: string[]; timeoutMinutes?: number }) => {
      if (conversationId === undefined) {
        throw new Error('conversationId obrigatório para delegar etapas');
      }
      return api.delegateSteps(conversationId, body);
    },
    onSuccess: () => {
      if (conversationId === undefined) return;
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      useToastStore.getState().push({
        variant: 'success',
        title: 'Delegação aberta',
        description: 'A IA está conduzindo os objetivos delegados.',
        durationMs: 3500,
      });
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Falha ao delegar etapas',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });

  return {
    mutate: async (body) => {
      await mutation.mutateAsync(body);
    },
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
